'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Star, Trophy, Target, Lock, CheckCircle2, ChevronRight, ChevronDown, Swords, Brain, Flame } from 'lucide-react';
import { SKILL_TREE_ROLES, SkillBranch, SkillType, SkillTreeRoleConfig } from './skill-tree-config';
import { Character } from '@/lib/iam-service';
import { CharacterRole } from '@/types/enums';

interface CharacterProgressionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  character?: Character;
  globalMp: number;
}

export function CharacterProgressionModal({
  open,
  onOpenChange,
  character,
  globalMp,
}: CharacterProgressionModalProps) {
  const [activeBranch, setActiveBranch] = useState<SkillBranch>('Cognitive');
  const [expandedRoles, setExpandedRoles] = useState<Record<string, boolean>>({});

  const toggleRole = (roleId: string) => {
    setExpandedRoles(prev => ({
      ...prev,
      [roleId]: !prev[roleId]
    }));
  };

  // Safe checks for arrays and objects
  const userRoles = character?.roles || [];
  const achievements = character?.achievements || [];
  const skills = character?.skills || {};

  const checkHasAchievement = (questName: string) => {
    return achievements.some(a => a.name === questName.split(' - ')[0] || a.name === questName);
  };

  const getSkillLevel = (skillName: string): number => {
    return skills[skillName] || 0;
  };

  const checkHasSkillLevel = (skillName: string, requiredLevel = 1) => {
    return getSkillLevel(skillName) >= requiredLevel;
  };

  const getRolesForBranch = (branch: SkillBranch) => {
    return SKILL_TREE_ROLES.filter(r => r.branch === branch);
  };

  const groupedBySkill = (roles: SkillTreeRoleConfig[]) => {
    const map = new Map<SkillType, SkillTreeRoleConfig[]>();
    for (const role of roles) {
      if (!map.has(role.prerequisiteSkill)) {
        map.set(role.prerequisiteSkill, []);
      }
      map.get(role.prerequisiteSkill)!.push(role);
    }
    return map;
  };

  const branchIcons: Record<SkillBranch, React.ReactNode> = {
    Cognitive: <Brain className="w-5 h-5 text-blue-500" />,
    Emotional: <Flame className="w-5 h-5 text-rose-500" />,
    Practical: <Swords className="w-5 h-5 text-amber-500" />
  };

  const branchColors: Record<SkillBranch, string> = {
    Cognitive: 'border-blue-500/30 bg-blue-500/5',
    Emotional: 'border-rose-500/30 bg-rose-500/5',
    Practical: 'border-amber-500/30 bg-amber-500/5'
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-5xl max-h-[90vh] flex flex-col p-0 overflow-hidden bg-background/95 backdrop-blur-xl border-primary/20">
        
        {/* Header */}
        <div className="flex-none p-6 border-b border-border/50 bg-muted/20">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between text-2xl font-light tracking-wide">
              <span>Character Progression</span>
              <div className="flex items-center gap-3">
                <div className="flex flex-col items-end">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Global MP</span>
                  <div className="flex items-center gap-1.5 text-amber-500">
                    <Star className="w-5 h-5 fill-amber-500" />
                    <span className="text-xl font-bold font-mono">{globalMp}</span>
                  </div>
                </div>
              </div>
            </DialogTitle>
            <DialogDescription>
              Master skills and complete achievements to unlock professional roles.
            </DialogDescription>
          </DialogHeader>

          {/* Branch Navigation */}
          <div className="flex gap-2 mt-6">
            {(['Cognitive', 'Emotional', 'Practical'] as SkillBranch[]).map(branch => (
              <button
                key={branch}
                onClick={() => setActiveBranch(branch)}
                className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-all ${
                  activeBranch === branch 
                    ? `border-primary/50 bg-primary/10 shadow-[0_0_15px_rgba(var(--primary),0.15)]` 
                    : 'border-border/50 hover:bg-muted/50 opacity-60 hover:opacity-100'
                }`}
              >
                {branchIcons[branch]}
                <span className="font-semibold uppercase tracking-wider text-sm">{branch}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          
          {Array.from(groupedBySkill(getRolesForBranch(activeBranch)).entries()).map(([skillName, roles]) => {
            
            const skillLevel = getSkillLevel(skillName);
            
            return (
              <div key={skillName} className={`rounded-xl border-2 p-5 ${branchColors[activeBranch]}`}>
                
                {/* Skill Header */}
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-border/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-background border shadow-sm flex items-center justify-center">
                      <Target className="w-5 h-5 text-foreground/70" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold uppercase tracking-widest">{skillName.replace(/_/g, ' ')}</h3>
                      <p className="text-xs text-muted-foreground font-medium">Pre-requisite Skill</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-mono font-bold">{skillLevel}<span className="text-sm text-muted-foreground">/10</span></div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Skill Level</div>
                  </div>
                </div>

                {/* Roles under this Skill */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {roles.map(role => {
                    const hasMp = globalMp >= role.mpRequirement;
                    const hasSkill = checkHasSkillLevel(role.prerequisiteSkill, 1);
                    const hasQuest = checkHasAchievement(role.achievementQuest);
                    
                    // Technically it unlocks automatically, but we check if they have the role directly or meet the reqs
                    const isUnlocked = userRoles.includes(role.id) || (hasMp && hasSkill && hasQuest);
                    const isExpanded = expandedRoles[role.id];

                    return (
                      <Card 
                        key={role.id} 
                        className={`overflow-hidden transition-all duration-300 border-2 ${
                          isUnlocked 
                            ? 'border-primary/40 bg-primary/5 hover:border-primary/60' 
                            : 'border-border/50 bg-background hover:border-border'
                        }`}
                      >
                        <button
                          onClick={() => toggleRole(role.id)}
                          className="w-full text-left p-4 flex items-center justify-between group"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isUnlocked ? 'bg-primary/20' : 'bg-muted'}`}>
                              {isUnlocked ? <CheckCircle2 className="w-4 h-4 text-primary" /> : <Lock className="w-4 h-4 text-muted-foreground" />}
                            </div>
                            <div>
                              <h4 className={`font-bold uppercase tracking-wider ${isUnlocked ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {role.id}
                              </h4>
                              <p className="text-[10px] text-muted-foreground font-medium">Professional Role</p>
                            </div>
                          </div>
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />}
                        </button>

                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="px-4 pb-4 space-y-3 pt-2 border-t border-border/40 bg-muted/10">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">Unlock Requirements</p>
                                
                                <div className="flex items-center gap-2 text-sm">
                                  {hasMp ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" /> : <Lock className="w-4 h-4 text-muted-foreground/50 shrink-0" />}
                                  <span className={hasMp ? 'text-foreground' : 'text-muted-foreground'}>
                                    <strong className="text-amber-500 font-mono">{role.mpRequirement} MP</strong> (Global)
                                  </span>
                                </div>
                                
                                <div className="flex items-center gap-2 text-sm">
                                  {hasSkill ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" /> : <Lock className="w-4 h-4 text-muted-foreground/50 shrink-0" />}
                                  <span className={hasSkill ? 'text-foreground' : 'text-muted-foreground'}>
                                    Level 1+ <strong>{role.prerequisiteSkill.replace(/_/g, ' ')}</strong>
                                  </span>
                                </div>
                                
                                <div className="flex items-start gap-2 text-sm">
                                  {hasQuest ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" /> : <Lock className="w-4 h-4 text-muted-foreground/50 shrink-0 mt-0.5" />}
                                  <span className={hasQuest ? 'text-foreground' : 'text-muted-foreground'}>
                                    <span className="flex items-center gap-1.5"><Trophy className="w-3 h-3" /> Quest:</span>
                                    <span className="text-xs">{role.achievementQuest}</span>
                                  </span>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                      </Card>
                    );
                  })}
                </div>

              </div>
            );
          })}

        </div>
      </DialogContent>
    </Dialog>
  );
}
